import { z } from "zod";
import { IdSchema } from "./common.js";

const NonEmpty = z.string().min(1);
const Link = z.object({ label: NonEmpty, href: NonEmpty.regex(/^(\/|https:\/\/)/) }).strict();

export const LpProposalContentSchema = z
	.object({
		siteId: z.enum(["default", "lp", "newbiz", "creator"]),
		hero: z
			.object({
				badge: NonEmpty,
				title: z.array(NonEmpty).min(1),
				description: NonEmpty,
				primary: NonEmpty,
				secondary: NonEmpty,
				note: NonEmpty,
				chatLabel: NonEmpty,
				chat: z
					.array(z.object({ speaker: z.enum(["agent", "user"]), text: NonEmpty }).strict())
					.min(1),
				choices: z.array(NonEmpty).min(1),
			})
			.strict(),
		difference: z
			.object({
				title: NonEmpty,
				items: z.array(z.object({ title: NonEmpty, body: NonEmpty }).strict()).min(1),
			})
			.strict(),
		how: z
			.object({ title: NonEmpty, steps: z.array(z.object({ title: NonEmpty }).strict()).length(6) })
			.strict(),
		industries: z
			.object({
				title: NonEmpty,
				description: NonEmpty,
				allLabel: NonEmpty,
				priorityLabel: NonEmpty,
				scopeLabel: NonEmpty,
				items: z
					.array(
						z
							.object({
								key: NonEmpty,
								name: NonEmpty,
								tagline: NonEmpty,
								scope: NonEmpty,
								priority: z.boolean(),
							})
							.strict(),
					)
					.min(1),
			})
			.strict(),
		pricing: z.object({ title: NonEmpty, status: z.literal("準備中") }).strict(),
		cta: z
			.object({ title: NonEmpty, description: NonEmpty, primary: NonEmpty, secondary: NonEmpty })
			.strict(),
		trust: z
			.object({
				title: NonEmpty,
				operatorLabel: NonEmpty,
				operator: z.literal("Agentry 運営事務局"),
				operatorConfirm: z.literal("pending_owner"),
				legalLinks: z.array(Link).min(1),
				aiNotice: NonEmpty,
			})
			.strict(),
	})
	.strict();

export const LpProposalSchema = z
	.object({
		schemaVersion: z.literal(1),
		siteId: z.enum(["default", "lp", "newbiz", "creator"]),
		content: LpProposalContentSchema,
		provenance: z.array(
			z
				.object({
					outputPath: NonEmpty.regex(/^\//),
					sourceArtifactId: IdSchema,
					sourcePath: NonEmpty.regex(/^\//),
				})
				.strict(),
		),
	})
	.strict()
	.refine((value) => value.siteId === value.content.siteId, {
		message: "proposal and content siteId must match",
		path: ["content", "siteId"],
	});

export const LpProposalSetSchema = z
	.object({
		siteId: z.enum(["default", "lp", "newbiz", "creator"]),
		intakeArtifactId: IdSchema,
		effectiveWorkflowHash: z.string().regex(/^[0-9a-f]{64}$/),
		proposals: z
			.array(z.object({ artifactId: IdSchema, version: z.number().int().min(1) }).strict())
			.length(3),
		designGate: z
			.object({ ng: z.number().int().min(0), cta: z.boolean(), evalV2: z.number().min(0).max(1) })
			.strict(),
	})
	.strict();

export const LpEvalV2CriterionSchema = z
	.object({
		id: NonEmpty,
		pass: z.boolean(),
		evidenceQuote: NonEmpty.max(180),
		rationale: NonEmpty,
	})
	.strict();

export const LpEvalV2ResultSchema = z
	.object({ criteria: z.array(LpEvalV2CriterionSchema).length(18), summary: NonEmpty })
	.strict();

export type LpProposal = z.infer<typeof LpProposalSchema>;
export type LpProposalSet = z.infer<typeof LpProposalSetSchema>;
